import React, { useEffect, useRef, useState } from 'react';
import { useTheme } from '../contexts/ThemeContext';
import { ProcessLogsModal, ProcessStatusCard } from './ProcessLogSummary';
import { RESUME_LIBRARY_LABEL } from '../utils/uxLabels';
import { aiAutomationApi } from '../utils/aiAutomationApi';
import { getCid } from '../utils/encryptedStorage';
import {
  findWorkflowSnapshot,
  workflowCount,
  workflowStateToLegacyStatus,
  workflowSyntheticLog,
} from '../utils/workflowSnapshot';

const PlatformIcon = ({ platform }) => {
  if (platform === 'LinkedIn') {
    return <span style={{ fontSize: 15, fontWeight: 800 }}>in</span>;
  }
  if (platform === RESUME_LIBRARY_LABEL) {
    return <span style={{ fontSize: 15, fontWeight: 800 }}>R</span>;
  }
  return <span style={{ fontSize: 15, fontWeight: 800 }}>G</span>;
};

/** Convert file log entries to the frontend log format for ProcessLogsModal/steps */
const fileLogsToEntries = (fileLogs) => {
  if (!Array.isArray(fileLogs)) return [];
  return fileLogs.map((entry) => ({
    message: entry.message || '',
    logMessage: entry.message || '',
    logType: entry.log_type === 'error' ? 'error' : entry.log_type === 'success' ? 'success' : entry.log_type === 'warning' ? 'warning' : 'info',
    type: entry.log_type === 'error' ? 'error' : 'info',
    status: entry.log_type === 'error' ? 'error' : entry.log_type === 'success' ? 'completed' : 'in_progress',
    timestamp: entry.timestamp || '',
    platform: entry.platform || '',
    phase: entry.phase || '',
  }));
};

const normalizeSelectedSourcingChannel = (rawChannel) => {
  const normalized = String(rawChannel || '').trim().toLowerCase();
  if (!normalized) return null;
  if (normalized.includes('linkedin')) return 'linkedin';
  if (normalized.includes('google') || normalized.includes('online') || normalized.includes('searxng')) return 'google';
  if (normalized.includes('resumebox') || normalized.includes('resume box') || normalized.includes('resume_box') || normalized.includes('resume')) return 'resumebox';
  return null;
};

/** Renders the full session-file log section inside a sourcing ProcessLogsModal */
const SourcingFullFileLogSection = ({ fileState, isDark, onRefetch }) => {
  if (!fileState) return null;
  const { loading, logs, error, fetched } = fileState;

  const logTypeColor = (logType) => {
    if (logType === 'error') return isDark ? '#FCA5A5' : '#DC2626';
    if (logType === 'success') return isDark ? '#6EE7B7' : '#16A34A';
    if (logType === 'warning') return isDark ? '#FDE68A' : '#CA8A04';
    return isDark ? '#94A3B8' : '#475569';
  };

  return (
    <div
      style={{
        marginTop: 20,
        borderTop: `1px solid ${isDark ? 'rgba(71,85,105,0.5)' : 'rgba(226,232,240,1)'}`,
        paddingTop: 14,
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 10 }}>
        <span style={{ fontSize: 12, fontWeight: 700, letterSpacing: '0.04em', textTransform: 'uppercase', color: isDark ? '#94A3B8' : '#64748B' }}>
          Full Session Log (File)
        </span>
        <button
          type="button"
          onClick={onRefetch}
          disabled={loading}
          style={{
            fontSize: 11, fontWeight: 700, padding: '4px 10px', borderRadius: 999,
            border: `1px solid ${isDark ? 'rgba(71,85,105,0.7)' : 'rgba(203,213,225,1)'}`,
            background: 'transparent', color: isDark ? '#CBD5E1' : '#475569',
            cursor: loading ? 'default' : 'pointer', opacity: loading ? 0.6 : 1,
          }}
        >
          {loading ? 'Loading…' : '↺ Refresh'}
        </button>
      </div>

      {loading && <div style={{ padding: '12px 0', color: isDark ? '#94A3B8' : '#64748B', fontSize: 13 }}>Loading full logs…</div>}
      {!loading && error && <div style={{ padding: '12px 0', color: isDark ? '#FCA5A5' : '#DC2626', fontSize: 13 }}>{error}</div>}
      {!loading && !error && fetched && logs.length === 0 && (
        <div style={{ padding: '12px 0', color: isDark ? '#94A3B8' : '#64748B', fontSize: 13 }}>No file logs found for this session yet.</div>
      )}
      {!loading && !error && logs.length > 0 && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
          {logs.map((entry, idx) => (
            <div key={idx} style={{ display: 'flex', gap: 10, alignItems: 'flex-start', padding: '5px 8px', borderRadius: 8, background: isDark ? 'rgba(30,41,59,0.5)' : 'rgba(248,250,252,0.9)', borderLeft: `3px solid ${logTypeColor(entry.log_type)}` }}>
              <span style={{ fontSize: 10, color: isDark ? '#64748B' : '#94A3B8', whiteSpace: 'nowrap', marginTop: 2, minWidth: 70 }}>
                {entry.timestamp ? new Date(entry.timestamp).toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit', second: '2-digit' }) : '—'}
              </span>
              <span style={{ fontSize: 12, color: isDark ? '#CBD5E1' : '#334155', flex: 1, wordBreak: 'break-word' }}>
                {entry.message}
              </span>
              {entry.log_type && entry.log_type !== 'info' && (
                <span style={{ fontSize: 10, fontWeight: 700, color: logTypeColor(entry.log_type), whiteSpace: 'nowrap', textTransform: 'uppercase' }}>
                  {entry.log_type}
                </span>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

const TalentScoutingResults = ({
  googleLogs = [],
  linkedinLogs = [],
  resumeBoxLogs = [],
  googleProcessing = false,
  linkedinProcessing = false,
  resumeBoxProcessing = false,
  googleCandidateCount = 0,
  linkedinCandidateCount = 0,
  resumeBoxCandidateCount = 0,
  selectedSourcingChannels = [],
  googleSearchFoundCount = 0,
  linkedinSearchFoundCount = 0,
  expandToken = 0,
  sessionId = '',
}) => {
  const { isDark } = useTheme();
  const [activeLogModal, setActiveLogModal] = useState(null);
  const [activityCollapsed, setActivityCollapsed] = useState(true);
  // File-based full logs per sourcing platform
  const [fileLogsState, setFileLogsState] = useState({
    google: { loading: false, logs: [], error: null, fetched: false },
    linkedin: { loading: false, logs: [], error: null, fetched: false },
    resumebox: { loading: false, logs: [], error: null, fetched: false },
  });
  const [fileLogsRefreshKey, setFileLogsRefreshKey] = useState(0);
  // Process tracking status fetched directly from hr_talent_gpt_process_tracking via API
  const [dbProcessStatus, setDbProcessStatus] = useState(null);
  const [workflowSnapshot, setWorkflowSnapshot] = useState(null);
  // Key that increments whenever a sourcing channel completes — triggers a fresh DB status fetch
  // so stale mount-time data does not falsely mark a running channel as 'complete' or 'failed'.
  const [dbRefetchKey, setDbRefetchKey] = useState(0);
  const prevProcessingRef = useRef({ google: googleProcessing, linkedin: linkedinProcessing, resumebox: resumeBoxProcessing });

  // Re-fetch dbProcessStatus on sessionId change AND whenever any channel transitions from
  // processing → done, so the card reflects the up-to-date per-channel state immediately.
  // This is the authoritative source — no caching, no session state.
  useEffect(() => {
    if (!sessionId) {
      setDbProcessStatus(null);
      setWorkflowSnapshot(null);
      return;
    }
    let cancelled = false;

    const cid = getCid();
    if (cid) {
      aiAutomationApi
        .getWorkflowSnapshot(sessionId, cid)
        .then((data) => { if (!cancelled) setWorkflowSnapshot(data || null); })
        .catch(() => { if (!cancelled) setWorkflowSnapshot(null); });
    } else {
      setWorkflowSnapshot(null);
    }

    aiAutomationApi
      .get(`/process-status/${sessionId}`)
      .then((data) => { if (!cancelled) setDbProcessStatus(data || null); })
      .catch(() => { if (!cancelled) setDbProcessStatus(null); });
    return () => { cancelled = true; };
  }, [sessionId, dbRefetchKey]);

  // Trigger a DB re-fetch when any channel transitions from processing → done.
  // This prevents stale mount-time dbProcessStatus from incorrectly showing a running
  // channel as 'complete' (issue: LinkedIn shows Completed before it actually finishes).
  useEffect(() => {
    const prev = prevProcessingRef.current;
    const anyJustCompleted =
      (prev.google && !googleProcessing) ||
      (prev.linkedin && !linkedinProcessing) ||
      (prev.resumebox && !resumeBoxProcessing);
    prevProcessingRef.current = { google: googleProcessing, linkedin: linkedinProcessing, resumebox: resumeBoxProcessing };
    if (anyJustCompleted && sessionId) {
      setDbRefetchKey((k) => k + 1);
    }
  }, [googleProcessing, linkedinProcessing, resumeBoxProcessing, sessionId]);

  useEffect(() => {
    if (expandToken > 0) {
      setActivityCollapsed(false);
    }
  }, [expandToken]);

  // Fetch file-based full logs when a modal is opened
  useEffect(() => {
    if (!activeLogModal || !sessionId) return;

    // resumebox is also supported in the file logger
    const platformMap = { google: 'google', linkedin: 'linkedin', resumebox: 'resumebox' };
    const filePlatform = platformMap[activeLogModal];
    if (!filePlatform) return;
    const platformKey = activeLogModal;

    setFileLogsState((prev) => ({
      ...prev,
      [platformKey]: { loading: true, logs: prev[platformKey]?.logs || [], error: null, fetched: false },
    }));

    aiAutomationApi
      .get(`/logs/session/${sessionId}/sourcing/${filePlatform}`)
      .then((data) => {
        setFileLogsState((prev) => ({
          ...prev,
          [platformKey]: { loading: false, logs: data?.logs || [], error: null, fetched: true },
        }));
      })
      .catch(() => {
        setFileLogsState((prev) => ({
          ...prev,
          [platformKey]: { loading: false, logs: prev[platformKey]?.logs || [], error: 'Could not load full log file.', fetched: true },
        }));
      });
  }, [activeLogModal, sessionId, fileLogsRefreshKey]);

  const googleWorkflow = findWorkflowSnapshot(workflowSnapshot, 'sourcing', 'google');
  const linkedinWorkflow = findWorkflowSnapshot(workflowSnapshot, 'sourcing', 'linkedin');
  const resumeBoxWorkflow = findWorkflowSnapshot(workflowSnapshot, 'sourcing', 'resumebox');
  const googleWorkflowStatus = workflowStateToLegacyStatus(googleWorkflow);
  const linkedinWorkflowStatus = workflowStateToLegacyStatus(linkedinWorkflow);
  const resumeBoxWorkflowStatus = workflowStateToLegacyStatus(resumeBoxWorkflow);
  const googleStatus = dbProcessStatus?.sourcing_google || googleWorkflowStatus || 'not_started';
  const linkedinStatus = dbProcessStatus?.sourcing_linkedin || linkedinWorkflowStatus || 'not_started';
  const resumeBoxStatus = dbProcessStatus?.sourcing_resumebox || resumeBoxWorkflowStatus || 'not_started';
  const googleCount = googleCandidateCount || workflowCount(googleWorkflow, ['candidate_count', 'profile_count', 'found_count']) || dbProcessStatus?.sourcing_google_count || 0;
  const linkedinCount = linkedinCandidateCount || workflowCount(linkedinWorkflow, ['candidate_count', 'profile_count', 'found_count']) || dbProcessStatus?.sourcing_linkedin_count || 0;
  const resumeBoxCount = resumeBoxCandidateCount || workflowCount(resumeBoxWorkflow, ['candidate_count', 'profile_count', 'found_count']) || dbProcessStatus?.sourcing_resumebox_count || 0;

  const selectedSourcingChannelSet = new Set(
    (Array.isArray(selectedSourcingChannels) ? selectedSourcingChannels : [])
      .map(normalizeSelectedSourcingChannel)
      .filter(Boolean),
  );
  const hasExplicitSourcingSelection = selectedSourcingChannelSet.size > 0;
  const isSelectedSourcingChannel = (channel) => (
    !hasExplicitSourcingSelection || selectedSourcingChannelSet.has(channel)
  );
  const googleHasActivity = googleLogs.length > 0 || googleProcessing || googleCount > 0 || (googleStatus && googleStatus !== 'not_started');
  const linkedinHasActivity = linkedinLogs.length > 0 || linkedinProcessing || linkedinCount > 0 || (linkedinStatus && linkedinStatus !== 'not_started');
  const resumeBoxHasActivity = resumeBoxLogs.length > 0 || resumeBoxProcessing || resumeBoxCount > 0 || (resumeBoxStatus && resumeBoxStatus !== 'not_started');

  const showGoogle = isSelectedSourcingChannel('google') && (hasExplicitSourcingSelection || googleHasActivity);
  const showLinkedin = isSelectedSourcingChannel('linkedin') && (hasExplicitSourcingSelection || linkedinHasActivity);
  // Resume Library: show if props indicate activity OR DB table says it has activity
  const showResumeBox = isSelectedSourcingChannel('resumebox') && (hasExplicitSourcingSelection || resumeBoxHasActivity);

  // Build a synthetic log entry from DB status when props logs are empty
  const makeSyntheticLog = (chStatus) => [{
    message:
      chStatus === 'complete' ? 'Sourcing completed successfully.' :
      chStatus === 'failed'   ? 'Sourcing encountered an issue.'   :
                               'Sourcing is in progress.',
    timestamp: new Date().toISOString(),
    type:    chStatus === 'complete' ? 'completed' : chStatus === 'failed' ? 'error' : 'info',
    logType: chStatus === 'complete' ? 'completed' : chStatus === 'failed' ? 'error' : 'info',
    status:  chStatus === 'complete' ? 'completed' : chStatus === 'failed' ? 'error' : 'info',
    platform: null,
  }];
  const makeWorkflowAwareLog = (row, chStatus, fallbackMessage) => (
    workflowSyntheticLog(row, fallbackMessage) || makeSyntheticLog(chStatus)[0]
  );

  // Effective logs: prefer props (set by ChatPage from localStorage/file API), fall back to synthetic from DB status
  const effectiveGoogleLogs    = googleLogs.length > 0    ? googleLogs    : (googleStatus    && googleStatus    !== 'not_started' ? [makeWorkflowAwareLog(googleWorkflow, googleStatus, 'Online sourcing status updated.')] : []);
  const effectiveLinkedinLogs  = linkedinLogs.length > 0  ? linkedinLogs  : (linkedinStatus  && linkedinStatus  !== 'not_started' ? [makeWorkflowAwareLog(linkedinWorkflow, linkedinStatus, 'LinkedIn sourcing status updated.')] : []);
  const effectiveResumeBoxLogs = resumeBoxLogs.length > 0
    ? resumeBoxLogs
    : (resumeBoxStatus && resumeBoxStatus !== 'not_started'
      ? [makeWorkflowAwareLog(resumeBoxWorkflow, resumeBoxStatus, `${RESUME_LIBRARY_LABEL} status updated.`)]
      // If count > 0 but dbProcessStatus not yet loaded, synthesise a completion log so the
      // card doesn't briefly flash "This source has not started yet."
      : resumeBoxCount > 0
        ? [{ message: 'Sourcing completed successfully.', timestamp: new Date().toISOString(), type: 'completed', logType: 'completed', status: 'completed', platform: null }]
        : []);

  // Effective processing flags.
  // DB/workflow status is the source of truth for terminal state. Candidate counts are display-only:
  // LinkedIn can stream candidates while extraction is still running, so a non-zero count is not completion.
  const _isGoogleTerminal    = googleStatus    === 'complete' || googleStatus    === 'failed';
  const _isLinkedinTerminal  = linkedinStatus  === 'complete' || linkedinStatus  === 'failed';
  const _isResumeBoxTerminal = resumeBoxStatus === 'complete' || resumeBoxStatus === 'failed';
  const effectiveGoogleProcessing    = _isGoogleTerminal    ? false : (googleStatus === 'processing' || googleProcessing);
  const effectiveLinkedinProcessing  = _isLinkedinTerminal  ? false : (linkedinStatus === 'processing' || linkedinProcessing);
  const effectiveResumeBoxProcessing = _isResumeBoxTerminal ? false : (resumeBoxStatus === 'processing' || resumeBoxProcessing);

  const processCards = [
    showResumeBox
      ? {
          key: 'resumebox',
          title: RESUME_LIBRARY_LABEL,
          metric: (() => {
            const cnt = resumeBoxCount;
            if (cnt > 0) return `${cnt} profile${cnt === 1 ? '' : 's'} available`;
            return resumeBoxStatus === 'complete' ? '0 profiles found' : 'Checking local candidate library';
          })(),
          logs: effectiveResumeBoxLogs,
          processing: effectiveResumeBoxProcessing,
          statusOverride: effectiveResumeBoxProcessing ? 'processing'
            : resumeBoxStatus === 'complete' ? 'success'
            : resumeBoxStatus === 'failed' ? 'failed' : null,
        }
      : null,
    showGoogle
      ? {
          key: 'google',
          title: 'Online Sourcing',
          metric: (() => {
            const cnt = googleCount;
            if (cnt > 0) return `${cnt} profile${cnt === 1 ? '' : 's'} found`;
            return 'Web discovery and fit screening';
          })(),
          logs: effectiveGoogleLogs,
          processing: effectiveGoogleProcessing,
          statusOverride: effectiveGoogleProcessing ? 'processing'
            : googleStatus === 'complete' ? 'success'
            : googleStatus === 'failed' ? 'failed' : null,
        }
      : null,
    showLinkedin
      ? {
          key: 'linkedin',
          title: 'LinkedIn Sourcing',
          metric: (() => {
            const cnt = linkedinCount;
            if (cnt > 0) return `${cnt} profile${cnt === 1 ? '' : 's'} found`;
            return 'Professional profile discovery';
          })(),
          logs: effectiveLinkedinLogs,
          processing: effectiveLinkedinProcessing,
          statusOverride: effectiveLinkedinProcessing ? 'processing'
            : linkedinStatus === 'complete' ? 'success'
            : linkedinStatus === 'failed' ? 'failed' : null,
        }
      : null,
  ].filter(Boolean);

  const totalFound = processCards.reduce((sum, card) => {
    if (card.key === 'google') return sum + Number(googleSearchFoundCount || googleCount || 0);
    if (card.key === 'linkedin') return sum + Number(linkedinSearchFoundCount || linkedinCount || 0);
    if (card.key === 'resumebox') return sum + Number(resumeBoxCount || 0);
    return sum;
  }, 0);
  const totalUpdates = processCards.reduce((sum, card) => sum + (card.logs?.length || 0), 0);
  const isProcessing = processCards.some((card) => card.processing);

  return (
    <div className="chat_spacing_md">
      <div
        style={{
          borderRadius: 16,
          border: `1px solid ${isDark ? 'rgba(71, 85, 105, 0.5)' : 'rgba(177, 196, 232, 0.9)'}`,
          background: isDark
            ? 'rgba(15, 23, 42, 0.82)'
            : 'linear-gradient(135deg, rgba(255,255,255,0.99) 0%, rgba(241,248,255,0.98) 100%)',
          boxShadow: isDark ? 'none' : '0 10px 24px rgba(59, 91, 219, 0.08)',
          padding: '10px 12px',
        }}
      >
        <button
          type="button"
          onClick={() => setActivityCollapsed((prev) => !prev)}
          style={{
            width: '100%',
            border: 'none',
            background: 'transparent',
            padding: 0,
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 12,
            textAlign: 'left',
          }}
        >
          <div style={{ minWidth: 0 }}>
            <h3 className="chat_section_heading" style={{ margin: 0 }}>
              Sourcing Activity
            </h3>
            <p style={{ fontSize: 12, color: isDark ? '#94A3B8' : '#64748B', margin: '4px 0 0' }}>
              {isProcessing ? 'Processing' : 'Latest activity'} · {totalUpdates} update{totalUpdates === 1 ? '' : 's'} · {totalFound || 0} profile{totalFound === 1 ? '' : 's'}
            </p>
          </div>
          <span
            style={{
              flexShrink: 0,
              borderRadius: 999,
              padding: '6px 10px',
              color: '#fff',
              fontSize: 12,
              fontWeight: 700,
              background: 'linear-gradient(135deg, #3b5bdb 0%, #28aca8 100%)',
            }}
          >
            {activityCollapsed ? 'View activity' : 'Hide activity'}
          </span>
        </button>

        {!activityCollapsed && (
          <div
            className="mt-3"
            style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(min(100%, 240px), 1fr))',
              gap: 10,
            }}
          >
            {processCards.map((card) => (
              <ProcessStatusCard
                key={card.key}
                title={card.title}
                icon={<PlatformIcon platform={card.title} />}
                metric={card.metric}
                logs={card.logs}
                processing={card.processing}
                completedLabel="Completed"
                idleLabel="Queued"
                emptySummary="This source has not started yet."
                isDark={isDark}
                statusOverride={card.statusOverride}
                onViewLogs={() => setActiveLogModal(card.key)}
              />
            ))}
          </div>
        )}
      </div>

      <ProcessLogsModal
        open={activeLogModal === 'google'}
        title="Online Sourcing Log"
        logs={
          fileLogsState.google.fetched && fileLogsToEntries(fileLogsState.google.logs).length > 0
            ? fileLogsToEntries(fileLogsState.google.logs)
            : googleLogs
        }
        isDark={isDark}
        onClose={() => setActiveLogModal(null)}
      >
        <SourcingFullFileLogSection
          fileState={fileLogsState.google}
          isDark={isDark}
          onRefetch={() => setFileLogsRefreshKey((key) => key + 1)}
        />
      </ProcessLogsModal>

      <ProcessLogsModal
        open={activeLogModal === 'linkedin'}
        title="LinkedIn Sourcing Log"
        logs={
          fileLogsState.linkedin.fetched && fileLogsToEntries(fileLogsState.linkedin.logs).length > 0
            ? fileLogsToEntries(fileLogsState.linkedin.logs)
            : linkedinLogs
        }
        isDark={isDark}
        onClose={() => setActiveLogModal(null)}
      >
        <SourcingFullFileLogSection
          fileState={fileLogsState.linkedin}
          isDark={isDark}
          onRefetch={() => setFileLogsRefreshKey((key) => key + 1)}
        />
      </ProcessLogsModal>

      <ProcessLogsModal
        open={activeLogModal === 'resumebox'}
        title={`${RESUME_LIBRARY_LABEL} Log`}
        logs={
          fileLogsState.resumebox.fetched && fileLogsToEntries(fileLogsState.resumebox.logs).length > 0
            ? fileLogsToEntries(fileLogsState.resumebox.logs)
            : resumeBoxLogs
        }
        isDark={isDark}
        onClose={() => setActiveLogModal(null)}
      >
        <SourcingFullFileLogSection
          fileState={fileLogsState.resumebox}
          isDark={isDark}
          onRefetch={() => setFileLogsRefreshKey((key) => key + 1)}
        />
      </ProcessLogsModal>
    </div>
  );
};

export default TalentScoutingResults;
